import { useEffect, useState } from "react";
import { SidebarProvider, SidebarTrigger } from "../components/ui/sidebar";
import { AppSidebar } from "../components/AppSidebar";
import { Button } from "../components/ui/button";
import { LogOut, Languages } from "lucide-react";
import { useNavigate } from "react-router-dom";
import defaultBg from "../assets/dashboard-bg-gen.png";
import { useLanguage } from "../contexts/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

export function DashboardLayout({ children, backgroundImage }) {
  const navigate = useNavigate();
  const { t, language, setLanguage } = useLanguage();
  const [langOpen, setLangOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("bv.registerResponse");
    navigate("/");
  };

  useEffect(() => {
    const hasSession = (() => {
      try {
        return !!localStorage.getItem("bv.registerResponse");
      } catch {
        return false;
      }
    })();
    if (!hasSession) {
      navigate("/");
    }
  }, [navigate]);

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <div
          className="flex-1 flex flex-col min-w-0 relative bg-background"
          style={{
            backgroundImage:
              "linear-gradient(hsl(var(--border) / 0.3) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border) / 0.3) 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }}
        >
          <header className="h-14 flex items-center justify-between border-b bg-sidebar text-sidebar-foreground px-4 relative z-10">
            <div className="flex items-center">
              <SidebarTrigger className="mr-4" />
              <h2 className="text-sm font-medium text-sidebar-primary">
                {t("Welcome back")}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLangOpen(true)}
                className="border-sidebar-border/50 bg-sidebar text-sidebar-foreground hover:bg-sidebar-accent"
              >
                <Languages className="h-4 w-4 mr-2" />
                {language === "fr" ? "FR" : "EN"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-sidebar-foreground/70 hover:text-sidebar-foreground"
              >
                <LogOut className="h-4 w-4 mr-2" />
                {t("Logout")}
              </Button>
            </div>
          </header>
          <main className="flex-1 p-6 overflow-auto relative z-10">
            {children}
          </main>
        </div>
      </div>

      <Dialog open={langOpen} onOpenChange={setLangOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">
              {t("Select language")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Button
              variant={language === "en" ? "default" : "outline"}
              className="w-full justify-start"
              onClick={() => {
                setLanguage("en");
                setLangOpen(false);
              }}
            >
              {t("English")}
            </Button>
            <Button
              variant={language === "fr" ? "default" : "outline"}
              className="w-full justify-start"
              onClick={() => {
                setLanguage("fr");
                setLangOpen(false);
              }}
            >
              {t("French")}
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLangOpen(false)}>
              {t("Cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  );
}
